import { generateId } from "@fleet/utils";
import { createRoute, Link } from "@tanstack/solid-router";
import { createEffect, createSignal, Show } from "solid-js";
import { chronosRoute } from "../chronos";
import { GlassSurface, PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { createPowerSyncSingle } from "@time-tracker/shared/solid";
import { and, eq, pomodoro_config, type PomodoroConfig } from "@time-tracker/powersync";
import { AlarmClockOff, History, Settings, Target, Trash2 } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { WorkspaceTagsManager } from "@/features/chronos-sync/components/workspace-tags-manager";
import { getWeeklyGoalHours, setWeeklyGoalHours } from "@/features/chronos-sync/weekly-goal";
import {
  getIdleThresholdMinutes,
  setIdleThresholdMinutes,
} from "@/features/chronos-sync/idle-threshold";
import {
  livePomodoroConfigFilters,
  MAX_IDLE_THRESHOLD_MINUTES,
  MAX_WEEKLY_GOAL_HOURS,
  patchRowVerified,
  queryKeys,
} from "@time-tracker/shared";

const DEFAULT_FOCUS_DURATION = 25;
const DEFAULT_SHORT_BREAK_DURATION = 5;
const DEFAULT_LONG_BREAK_DURATION = 15;
const DEFAULT_LONG_BREAK_INTERVAL = 4;
const DEFAULT_DAILY_GOAL = 8;

const MIN_VAL_1 = 1;
const MAX_FOCUS_VAL = 180;
const MAX_SHORT_BREAK_VAL = 60;
const MAX_LONG_BREAK_VAL = 120;
const MAX_INTERVAL_VAL = 12;
const MAX_GOAL_VAL = 24;
const RADIX_DECIMAL = 10;

function ChronosSettingsPage() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  const { t } = useI18n();

  const [focusDuration, setFocusDuration] = createSignal(DEFAULT_FOCUS_DURATION);
  const [shortBreakDuration, setShortBreakDuration] = createSignal(DEFAULT_SHORT_BREAK_DURATION);
  const [longBreakDuration, setLongBreakDuration] = createSignal(DEFAULT_LONG_BREAK_DURATION);
  const [longBreakInterval, setLongBreakInterval] = createSignal(DEFAULT_LONG_BREAK_INTERVAL);
  const [dailyGoal, setDailyGoal] = createSignal(DEFAULT_DAILY_GOAL);
  const [isSaving, setIsSaving] = createSignal(false);

  // Weekly tracked-hours goal — a device-local preference (localStorage), not
  // synced. Hydrated once on mount; persisted by its own save button.
  const [weeklyGoal, setWeeklyGoal] = createSignal(getWeeklyGoalHours());

  const handleSaveWeeklyGoal = () => {
    setWeeklyGoalHours(weeklyGoal());
    setWeeklyGoal(getWeeklyGoalHours());
    showToast(t(msg`Weekly goal saved`), "success");
  };

  // Idle-detection threshold — device-local preference (localStorage), consumed
  // live by the always-mounted IdleGuard. 0 = detection off.
  const [idleThreshold, setIdleThreshold] = createSignal(getIdleThresholdMinutes());

  const handleSaveIdleThreshold = () => {
    setIdleThresholdMinutes(idleThreshold());
    setIdleThreshold(getIdleThresholdMinutes());
    showToast(t(msg`Idle detection saved`), "success");
  };

  // Subscribe to pomodoro config
  const configQuery = createPowerSyncSingle<PomodoroConfig>({
    queryKey: () => queryKeys.chronos.pomodoroConfig(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance
        .select()
        .from(pomodoro_config)
        .where(and(eq(pomodoro_config.user_id, userId()), ...livePomodoroConfigFilters()))
        .limit(1);
    },
    enabled: () => Boolean(userId()) && Boolean(drizzle()),
    suspense: false,
  });

  // Sync component signals when database config loads
  createEffect(() => {
    const config = configQuery.data;
    if (config) {
      setFocusDuration(config.focus_duration ?? DEFAULT_FOCUS_DURATION);
      setShortBreakDuration(config.short_break_duration ?? DEFAULT_SHORT_BREAK_DURATION);
      setLongBreakDuration(config.long_break_duration ?? DEFAULT_LONG_BREAK_DURATION);
      setLongBreakInterval(config.long_break_interval ?? DEFAULT_LONG_BREAK_INTERVAL);
      setDailyGoal(config.daily_goal ?? DEFAULT_DAILY_GOAL);
    }
  });

  const handleSave = async (e: Event) => {
    e.preventDefault();
    const dbInstance = drizzle();
    if (!dbInstance) {
      showToast(t(msg`Database not ready`), "error");
      return;
    }

    setIsSaving(true);
    try {
      const currentConfig = configQuery.data;
      const queryPromise = currentConfig
        ? patchRowVerified(dbInstance, pomodoro_config, pomodoro_config.id, currentConfig.id, {
            focus_duration: focusDuration(),
            short_break_duration: shortBreakDuration(),
            long_break_duration: longBreakDuration(),
            long_break_interval: longBreakInterval(),
            daily_goal: dailyGoal(),
          })
        : dbInstance.insert(pomodoro_config).values({
            id: generateId(),
            user_id: userId(),
            focus_duration: focusDuration(),
            short_break_duration: shortBreakDuration(),
            long_break_duration: longBreakDuration(),
            long_break_interval: longBreakInterval(),
            daily_goal: dailyGoal(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
      await queryPromise;
      showToast(t(msg`Configuration saved successfully`), "success");
    } catch (err) {
      console.error("Failed to save pomodoro configuration", err);
      showToast(t(msg`Failed to save configuration`), "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageContainer
      maxWidth="3xl"
      class="space-y-8 text-white"
      containerClass="bg-[#000000] text-white pb-[calc(6.5rem+var(--safe-area-inset-bottom))]"
    >
      <PageHeader
        title={t(msg`Chronos Settings`)}
        description={t(msg`Configure your Pomodoro blocks and focus targets.`)}
        icon={Settings}
      />

      <GlassSurface class="p-6 bg-[#121212] border border-white/10 rounded-[24px] shadow-none max-w-2xl mx-auto">
        <form onSubmit={handleSave} class="space-y-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="space-y-1.5">
              <label class="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                {t(msg`Focus Duration (minutes)`)}
                <input
                  id="focus-input"
                  type="number"
                  min={MIN_VAL_1}
                  max={MAX_FOCUS_VAL}
                  value={focusDuration()}
                  onInput={(e) =>
                    setFocusDuration(
                      Number.parseInt(e.currentTarget.value, RADIX_DECIMAL) ||
                        DEFAULT_FOCUS_DURATION,
                    )
                  }
                  class="w-full bg-background/50 border border-white/10 rounded-[16px] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 text-foreground placeholder-muted-foreground/60 mt-1.5 font-normal normal-case tracking-normal block"
                />
              </label>
            </div>

            <div class="space-y-1.5">
              <label class="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                {t(msg`Short Break Duration (minutes)`)}
                <input
                  id="short-break-input"
                  type="number"
                  min={MIN_VAL_1}
                  max={MAX_SHORT_BREAK_VAL}
                  value={shortBreakDuration()}
                  onInput={(e) =>
                    setShortBreakDuration(
                      Number.parseInt(e.currentTarget.value, RADIX_DECIMAL) ||
                        DEFAULT_SHORT_BREAK_DURATION,
                    )
                  }
                  class="w-full bg-background/50 border border-white/10 rounded-[16px] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 text-foreground placeholder-muted-foreground/60 mt-1.5 font-normal normal-case tracking-normal block"
                />
              </label>
            </div>

            <div class="space-y-1.5">
              <label class="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                {t(msg`Long Break Duration (minutes)`)}
                <input
                  id="long-break-input"
                  type="number"
                  min={MIN_VAL_1}
                  max={MAX_LONG_BREAK_VAL}
                  value={longBreakDuration()}
                  onInput={(e) =>
                    setLongBreakDuration(
                      Number.parseInt(e.currentTarget.value, RADIX_DECIMAL) ||
                        DEFAULT_LONG_BREAK_DURATION,
                    )
                  }
                  class="w-full bg-background/50 border border-white/10 rounded-[16px] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 text-foreground placeholder-muted-foreground/60 mt-1.5 font-normal normal-case tracking-normal block"
                />
              </label>
            </div>

            <div class="space-y-1.5">
              <label class="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                {t(msg`Long Break Interval (segments)`)}
                <input
                  id="interval-input"
                  type="number"
                  min={MIN_VAL_1}
                  max={MAX_INTERVAL_VAL}
                  value={longBreakInterval()}
                  onInput={(e) =>
                    setLongBreakInterval(
                      Number.parseInt(e.currentTarget.value, RADIX_DECIMAL) ||
                        DEFAULT_LONG_BREAK_INTERVAL,
                    )
                  }
                  class="w-full bg-background/50 border border-white/10 rounded-[16px] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 text-foreground placeholder-muted-foreground/60 mt-1.5 font-normal normal-case tracking-normal block"
                />
              </label>
            </div>

            <div class="space-y-1.5 md:col-span-2">
              <label class="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                {t(msg`Daily Segments Goal`)}
                <input
                  id="goal-input"
                  type="number"
                  min={MIN_VAL_1}
                  max={MAX_GOAL_VAL}
                  value={dailyGoal()}
                  onInput={(e) =>
                    setDailyGoal(
                      Number.parseInt(e.currentTarget.value, RADIX_DECIMAL) || DEFAULT_DAILY_GOAL,
                    )
                  }
                  class="w-full bg-background/50 border border-white/10 rounded-[16px] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 text-foreground placeholder-muted-foreground/60 mt-1.5 font-normal normal-case tracking-normal block"
                />
              </label>
            </div>
          </div>

          <div class="flex justify-end pt-4">
            <Button
              type="submit"
              disabled={isSaving() || !drizzle()}
              class="w-full md:w-32 bg-primary text-primary-foreground font-semibold py-2.5 rounded-full hover:bg-primary/90 transition-[transform,background-color] duration-200 will-change-transform active:scale-[0.98] flex items-center justify-center cursor-pointer"
            >
              <Show when={isSaving()} fallback={t(msg`Save`)}>
                {t(msg`Saving...`)}
              </Show>
            </Button>
          </div>
        </form>
      </GlassSurface>

      {/* Weekly tracked-hours goal — device-local target surfaced on the dashboard. */}
      <GlassSurface class="p-6 bg-[#121212] border border-white/10 rounded-[24px] shadow-none max-w-2xl mx-auto">
        <div class="mb-4 flex items-center gap-2">
          <Target class="h-4.5 w-4.5 text-primary" />
          <h2 class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t(msg`Weekly Goal`)}
          </h2>
        </div>
        <div class="flex flex-col gap-4 sm:flex-row sm:items-end">
          <div class="flex-1 space-y-1.5">
            <label class="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
              {t(msg`Target Hours per Week`)}
              <input
                id="weekly-goal-input"
                type="number"
                min={0}
                max={MAX_WEEKLY_GOAL_HOURS}
                step="0.5"
                value={weeklyGoal()}
                onInput={(e) => setWeeklyGoal(Number.parseFloat(e.currentTarget.value) || 0)}
                class="w-full bg-background/50 border border-white/10 rounded-[16px] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 text-foreground placeholder-muted-foreground/60 mt-1.5 font-normal normal-case tracking-normal block"
              />
            </label>
            <p class="text-[11px] text-muted-foreground normal-case tracking-normal">
              {t(msg`Set 0 to hide the weekly goal card on your dashboard.`)}
            </p>
          </div>
          <Button
            type="button"
            onClick={handleSaveWeeklyGoal}
            data-testid="weekly-goal-save"
            class="w-full sm:w-32 bg-primary text-primary-foreground font-semibold py-2.5 rounded-full hover:bg-primary/90 transition-[transform,background-color] duration-200 will-change-transform active:scale-[0.98] flex items-center justify-center cursor-pointer"
          >
            {t(msg`Save Goal`)}
          </Button>
        </div>
      </GlassSurface>

      {/* Idle detection — device-local threshold consumed by the IdleGuard. */}
      <GlassSurface class="p-6 bg-[#121212] border border-white/10 rounded-[24px] shadow-none max-w-2xl mx-auto">
        <div class="mb-4 flex items-center gap-2">
          <AlarmClockOff class="h-4.5 w-4.5 text-primary" />
          <h2 class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t(msg`Idle Detection`)}
          </h2>
        </div>
        <div class="flex flex-col gap-4 sm:flex-row sm:items-end">
          <div class="flex-1 space-y-1.5">
            <label class="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
              {t(msg`Minutes without activity before asking`)}
              <input
                id="idle-threshold-input"
                type="number"
                min={0}
                max={MAX_IDLE_THRESHOLD_MINUTES}
                step="1"
                value={idleThreshold()}
                onInput={(e) => setIdleThreshold(Number.parseFloat(e.currentTarget.value) || 0)}
                class="w-full bg-background/50 border border-white/10 rounded-[16px] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 text-foreground placeholder-muted-foreground/60 mt-1.5 font-normal normal-case tracking-normal block"
              />
            </label>
            <p class="text-[11px] text-muted-foreground normal-case tracking-normal">
              {t(
                msg`While a timer runs, we ask what to do with time you spent away. Set 0 to disable.`,
              )}
            </p>
          </div>
          <Button
            type="button"
            onClick={handleSaveIdleThreshold}
            data-testid="idle-threshold-save"
            class="w-full sm:w-32 bg-primary text-primary-foreground font-semibold py-2.5 rounded-full hover:bg-primary/90 transition-[transform,background-color] duration-200 will-change-transform active:scale-[0.98] flex items-center justify-center cursor-pointer"
          >
            {t(msg`Save`)}
          </Button>
        </div>
      </GlassSurface>

      <WorkspaceTagsManager />

      {/* ACTIVITY-CORE (discreto): historial/papelera viven aquí, no en la nav. */}
      <GlassSurface class="p-4 bg-[#121212] border border-white/10 rounded-[24px] shadow-none max-w-2xl mx-auto">
        <h2 class="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t(msg`History & recovery`)}
        </h2>
        <div class="flex flex-col gap-2">
          <Link
            to="/chronos/activity"
            class="flex items-center gap-3 rounded-2xl border border-white/10 px-4 py-3 text-sm font-medium transition-colors hover:bg-white/5"
            data-testid="settings-workspace-activity"
          >
            <History class="h-4.5 w-4.5 text-primary" />
            <span class="flex-1">{t(msg`Workspace activity`)}</span>
            <span class="text-xs text-muted-foreground">{t(msg`Who edited what`)}</span>
          </Link>
          <Link
            to="/chronos/trash"
            class="flex items-center gap-3 rounded-2xl border border-white/10 px-4 py-3 text-sm font-medium transition-colors hover:bg-white/5"
            data-testid="settings-trash"
          >
            <Trash2 class="h-4.5 w-4.5 text-primary" />
            <span class="flex-1">{t(msg`Trash`)}</span>
            <span class="text-xs text-muted-foreground">{t(msg`Restore deleted items`)}</span>
          </Link>
        </div>
      </GlassSurface>
    </PageContainer>
  );
}

export const chronosSettingsRoute = createRoute({
  getParentRoute: () => chronosRoute,
  path: "/settings",
  component: ChronosSettingsPage,
});
